"use client";

import * as React from "react";
import Section, { Reveal } from "../layout/Section";
import { CASE_STUDIES, SIDE_WORK, type CaseStudy } from "../../data/work";
import CoverMedia from "../ui/CoverMedia";
import FlowFieldSwarm from "../lab/FlowFieldSwarm";
import DataVizLab from "../lab/DataVizLab";
import ParityProblem from "./ParityProblem";

export default function Work() {
  return (
    <Section
      id="work"
      index="01"
      title="Selected work"
      lede="Two shipped games and two years of enterprise data visualization, with the working parts still running where you can poke at them."
    >
      <div className="space-y-14 md:space-y-28">
        {CASE_STUDIES.map((cs) => (
          <CaseStudyBlock key={cs.id} cs={cs} />
        ))}
      </div>

      <Reveal className="mt-20 md:mt-28">
        <div className="hairline-t pt-8">
          <p className="eyebrow">Also built</p>
          <ul className="mt-6 grid gap-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))] sm:grid-cols-2 lg:grid-cols-4">
            {SIDE_WORK.map((s) => (
              <li key={s.name} className="bg-bg">
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col p-5 no-underline transition-colors hover:bg-surface"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-medium">{s.name}</h3>
                    <span className="text-faint transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-accent">
                      <ExternalArrow />
                    </span>
                  </div>
                  <p className="prose-body mt-2 text-sm">{s.blurb}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {s.stack.map((tech) => (
                      <span key={tech} className="chip">
                        {tech}
                      </span>
                    ))}
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </Section>
  );
}

function CaseStudyBlock({ cs }: { cs: CaseStudy }) {
  const [open, setOpen] = React.useState(false);

  return (
    <article id={cs.id} className="anchor-offset scroll-mt-28">
      <Reveal>
        {/* header row */}
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 border-b pb-5">
          <span className="eyebrow">{cs.index}</span>
          <h3 className="text-2xl font-semibold tracking-tight md:text-3xl">
            {cs.name}
          </h3>
          {cs.status && <StatusPill status={cs.status} />}
          <p className="ml-auto font-mono text-2xs text-faint">
            {cs.org} · {cs.period}
          </p>
        </div>
        {/* Team shape, stated before anything else. It is the first thing a
            reviewer looks for and the easiest thing to bury in a paragraph. */}
        <p className="mt-3 font-mono text-2xs text-muted">{cs.scope}</p>
      </Reveal>

      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-14">
        {/* left: narrative */}
        <Reveal>
          <p className="eyebrow">{cs.kind}</p>
          <p className="mt-3 text-balance text-lg leading-snug tracking-tight text-ink">
            {cs.summary}
          </p>
          <p className="prose-body mt-4 text-[0.9375rem]">{cs.context}</p>

          <div className="mt-6 flex flex-wrap gap-1.5">
            {cs.stack.map((tech) => (
              <span key={tech} className="chip">
                {tech}
              </span>
            ))}
          </div>

          {cs.links && cs.links.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {cs.links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm"
                >
                  {l.label}
                  <ExternalArrow />
                </a>
              ))}
            </div>
          )}
        </Reveal>

        {/* right: imagery, with the numbers underneath it. The two columns
            used to end at very different heights; the figure is the shorter
            one, so the metrics balance it rather than stretch the narrative. */}
        <Reveal delay={80}>
          {cs.cover ? (
            <figure className="overflow-hidden rounded-xl border bg-raised">
              <CoverMedia cover={cs.cover} />
              {cs.shots && cs.shots.length > 0 && (
                <div className="hidden grid-cols-3 gap-px border-t bg-[rgb(var(--line)/var(--line-a))] sm:grid">
                  {cs.shots.map((s) => (
                    <img
                      key={s.src}
                      src={s.src}
                      alt={s.alt}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[16/10] w-full bg-raised object-cover"
                    />
                  ))}
                </div>
              )}
            </figure>
          ) : (
            <QualtricsPlate />
          )}

          <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))] sm:grid-cols-4">
            {cs.metrics.map((m) => (
              <div key={m.label} className="bg-bg px-4 py-3.5">
                <dt className="font-mono text-lg tracking-tight text-ink">
                  {m.value}
                </dt>
                <dd className="mt-1 text-2xs leading-snug text-faint">
                  {m.label}
                </dd>
              </div>
            ))}
          </dl>
          {cs.metricsNote && (
            <p className="mt-2 text-right font-mono text-2xs text-faint">
              {cs.metricsNote}
            </p>
          )}
        </Reveal>
      </div>

      {/* Live proof, sitting with the claim rather than in a section far
          below it. Only Swarmhold has one. */}
      {cs.id === "swarmhold" && (
        <Reveal delay={100}>
          <div className="mt-12">
            <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t pt-4">
              <p className="eyebrow">Proof, running here</p>
              <p className="prose-body max-w-2xl text-[0.875rem]">
                Push the agent and turret counts around and watch which numbers
                move. The field rebuild stays flat, because navigation is solved
                once for the grid rather than once per agent; the per-agent work
                scales, because every agent still has to be sampled, separated
                and collided.
              </p>
            </div>
            <FlowFieldSwarm />
          </div>
        </Reveal>
      )}

      {/* Qualtrics ships under NDA, so its proof is the craft itself: the same
          D3-and-React approach, over data I can publish. */}
      {cs.id === "qualtrics" && (
        <Reveal delay={100}>
          <div className="mt-12">
            <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t pt-4">
              <p className="eyebrow">Proof, running here</p>
              <p className="prose-body max-w-2xl text-[0.875rem]">
                Five encodings over real data from my own work, each built the
                way I built widgets on this team.
              </p>
            </div>
            <DataVizLab />
          </div>
        </Reveal>
      )}

      {/* Everything past the proof is opt-in. The main block and the demo
          carry the claim; the reasoning and the engineering detail sit behind
          one fold that says what is inside, so nobody is made to scroll
          through it and nobody has to guess what they are declining. */}
      <Reveal delay={120}>
        <div className="mt-10">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={`${cs.id}-more`}
            className="flex w-full items-start gap-4 rounded-xl border bg-raised p-5 text-left transition-colors hover:border-[rgb(var(--accent)/0.45)] md:p-6"
          >
            <span className="min-w-0 flex-1">
              <span className="eyebrow text-accent">
                {open ? "Less" : "More"}
              </span>
              <span className="prose-body mt-2 block max-w-2xl text-[0.875rem]">
                {cs.id === "swarmhold"
                  ? "The hard part: why every standard multiplayer answer failed here and what got built instead. Then how it was built, problem by problem."
                  : `How it was built: ${cs.details.length} problems, each as problem, approach and result.`}
              </span>
              <span className="mt-2 block text-2xs leading-relaxed text-faint">
                {cs.details.map((d) => d.title).join(" · ")}
              </span>
            </span>
            <Chevron open={open} className="mt-0.5" size={16} />
          </button>

          {open && (
            <div id={`${cs.id}-more`}>
              {/* The parity problem gets its own block: the constraint set is
                  the interesting part and a detail panel would flatten it. */}
              {cs.id === "swarmhold" && <ParityProblem />}

              <div className="mt-12">
                <p className="eyebrow mb-4 border-t pt-4">How it was built</p>
                <div className="grid gap-px overflow-hidden rounded-xl border bg-[rgb(var(--line)/var(--line-a))]">
                  {cs.details.map((d, i) => (
                    <DetailPanel key={d.title} detail={d} index={i} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Reveal>
    </article>
  );
}

function DetailPanel({
  detail,
  index,
}: {
  detail: CaseStudy["details"][number];
  index: number;
}) {
  return (
    <div className="bg-bg p-5 md:p-6">
      <div className="flex items-baseline gap-3">
        <span className="eyebrow">{String(index + 1).padStart(2, "0")}</span>
        <h4 className="text-[0.9375rem] font-medium tracking-tight">
          {detail.title}
        </h4>
      </div>
      {/* Setup on the left, work and outcome on the right. Read across:
          what was hard, what got built, what it bought. */}
      <div className="mt-4 grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-10">
        <DetailRow label="Problem" body={detail.problem} />
        <div className="space-y-5">
          <DetailRow label="Approach" body={detail.approach} />
          <DetailRow label="Result" body={detail.result} accent />
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent?: boolean;
}) {
  return (
    <div>
      <span
        className={`block font-mono text-2xs uppercase tracking-[0.12em] ${
          accent ? "text-accent" : "text-faint"
        }`}
      >
        {label}
      </span>
      <p
        className={`mt-1.5 max-w-[72ch] text-[0.875rem] leading-relaxed ${
          accent ? "text-ink" : "text-muted"
        }`}
      >
        {body}
      </p>
    </div>
  );
}

function StatusPill({ status }: { status: NonNullable<CaseStudy["status"]> }) {
  const dot =
    status.tone === "live"
      ? "bg-emerald-500"
      : status.tone === "active"
        ? "bg-accent"
        : "bg-[rgb(var(--faint))]";
  return (
    <span className="chip">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {status.label}
    </span>
  );
}

/**
 * Qualtrics work is under NDA and predates any screenshots I can publish, so
 * it gets an honest abstract plate rather than a fabricated mock.
 */
function QualtricsPlate() {
  const bars = [62, 88, 41, 74, 96, 55, 80, 34, 68, 90, 47, 72];
  return (
    <div className="relative overflow-hidden rounded-xl border bg-raised p-6">
      <div className="grid-field absolute inset-0 opacity-60" />
      <div className="relative flex h-full min-h-[16rem] flex-col justify-between gap-8">
        <div className="flex items-baseline justify-between">
          <p className="eyebrow">Widgets &amp; Data Visualization</p>
          <p className="font-mono text-2xs text-faint">2022 to 2024</p>
        </div>

        <div className="flex h-40 items-end gap-1.5" aria-hidden>
          {bars.map((v, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm"
              style={{
                height: `${v}%`,
                background:
                  i % 3 === 0
                    ? "rgb(var(--accent) / 0.7)"
                    : "rgb(var(--line) / 0.18)",
              }}
            />
          ))}
        </div>

        <p className="text-2xs leading-relaxed text-faint">
          Customer dashboards are under NDA, so there is no screenshot to show.
          The visualizations below are built the same way, with D3 for the
          scales and shape math and React owning the DOM.
        </p>
      </div>
    </div>
  );
}

function Chevron({
  open,
  size = 14,
  className = "",
}: {
  open: boolean;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden
      className={`shrink-0 text-faint transition-transform duration-300 ${className}`}
      style={{ transform: open ? "rotate(180deg)" : undefined }}
    >
      <path
        d="M4 6.5 8 10.5 12 6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExternalArrow() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M5.5 10.5 10.5 5.5M6.5 5.5h4v4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
